import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Check, ChevronRight, FilePlus, Lock, RefreshCw } from 'lucide-react';
import usePage from '@dashboard/hooks/usePage';
import { usePreview, getColorModeForScheme } from '@dashboard/context/PreviewContext';
import { Form } from '@dashboard/components/Formik';
import { themesKey } from '@/dashboard/lib/queryKeys';
import useThemeQueries from '@/dashboard/hooks/queries/useThemeQueries';
import { useQuery, useMutation, useQueryClient, notifyManager } from '@tanstack/react-query';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import { toast } from '@/dashboard/context/ToastContext';
import Button from '@/dashboard/components/Button';
import Divider from '@/dashboard/components/Divider';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { prompt } from '@/dashboard/lib/prompt';
import { confirm } from '@/dashboard/lib/confirm';
import storage from '@dashboard/services/storage';
import Collapsible from '@/dashboard/components/Collapsible';
import CanUseFeature, { useCanUseFeature } from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import Heading from '@/dashboard/components/Formik/components/Heading';
import { DESIGN_SECTION_ICONS } from './theming/designSectionIcons';
import { deepMergeWithBooleans } from '@repo/lib';
import { pageKey } from '@/dashboard/lib/queryKeys';
import usePageQueries from '@/dashboard/hooks/queries/usePageQueries';
import { getDesignFormFields } from '@/dashboard/scenes/PageEdit/scenes/PageDesign/theming/getDesignFormFields';
import { getCustomLabelCategories } from '@/dashboard/scenes/PageEdit/scenes/PageDesign/theming/customLabelFields';
import { renderDesignField } from './theming/designFieldRenderer';
import {
  designFieldPropsAreEqual,
  getDesignLeafFieldNames,
  designContainerChildrenPropsEqual,
  designContainerValuesEqual,
} from './theming/designFieldMemoization';
import logException from '@/dashboard/services/logException';
import { BASE_VARIABLES_DEFAULTS, stripSystemDefaults } from './theming/themeDefaults';
import { computeThemeOverrides } from './theming/computeThemeOverrides';
import { ThemeCardGrid } from './theming/ThemeCardGrid';
import { ThemeCardGridSkeleton } from './theming/ThemeCardGridSkeleton';
import { AllThemesGallery } from './theming/AllThemesGallery';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';

const EDITOR_MERGE_OPTIONS = { colorPairs: 'replaceWhenSet' };

/**
 * Recursively adds resetValue to canReset fields so the reset button
 * restores the base theme value instead of clearing to empty string.
 */
const enrichFieldsWithResetValues = (fieldConfig, baseLabels) => {
  if (fieldConfig.fields) {
    return {
      ...fieldConfig,
      fields: fieldConfig.fields.map(f => enrichFieldsWithResetValues(f, baseLabels)),
    };
  }
  if (fieldConfig.canReset && fieldConfig.name) {
    const labelKey = fieldConfig.name.replace('variables.labels.', '');
    return {
      ...fieldConfig,
      resetValue: baseLabels?.[labelKey] ?? '',
    };
  }
  return fieldConfig;
};

function CustomLabelsSection({
  formKey,
  editorState,
  baseThemeState,
  updateThemeOverrides,
  computeThemeOverrides,
  enrichFieldsWithResetValues,
  seller,
}) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handler = e => {
      if (e.detail === 'custom-labels') setIsOpen(true);
    };
    window.addEventListener('expand-design-section', handler);
    return () => window.removeEventListener('expand-design-section', handler);
  }, []);

  return (
    <div
      id="custom-labels"
      className="text-sm border-b border-gray-100 dark:border-border pb-4 mb-4"
    >
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <Collapsible.Trigger asChild>
          <button type="button" className="flex items-center gap-x-2 w-full group">
            <div className="flex-1 text-left">
              <div className="flex items-center gap-x-2 relative">
                <Heading
                  label="Custom labels"
                  level={3}
                  icon={DESIGN_SECTION_ICONS['custom-labels']}
                  hideLabel
                />
                <div className="flex items-center gap-1 absolute top-0 right-0 z-0">
                  <span className="p-1 h-6 w-6 flex items-center justify-center shrink-0">
                    <ChevronRight
                      className={clsx(
                        'h-3 w-3 text-gray-500 dark:text-muted-foreground transition-transform duration-200',
                        {
                          'rotate-90': isOpen,
                        },
                      )}
                    />
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
                Customize labels and copy.
              </div>
            </div>
          </button>
        </Collapsible.Trigger>

        <Collapsible.Content className="CollapsibleContentAnimation">
          <div className="mt-4">
            <Form
              key={`labels-${formKey}`}
              showState={false}
              autosave
              renderField={renderDesignField}
              customFieldPropsAreEqual={designFieldPropsAreEqual}
              customLeafFieldNames={getDesignLeafFieldNames}
              customContainerChildrenPropsEqual={designContainerChildrenPropsEqual}
              customContainerValuesEqual={designContainerValuesEqual}
              initialValues={{
                variables: {
                  labels: editorState.variables?.labels,
                },
              }}
              fieldsContainerClassName="flex flex-col gap-y-2"
              fields={getCustomLabelCategories({ seller }).map(category =>
                enrichFieldsWithResetValues(
                  { ...category, noMargin: true },
                  baseThemeState.variables?.labels,
                ),
              )}
              onSubmit={async values => {
                const updatedValues = {
                  ...editorState,
                  variables: {
                    ...editorState.variables,
                    labels: values.variables.labels,
                  },
                };
                const overrides = computeThemeOverrides(updatedValues, baseThemeState);

                await updateThemeOverrides.mutateAsync({
                  overrides: overrides,
                });

                return { data: null, error: null };
              }}
            />
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

function CustomCssSection({
  formKey,
  editorState,
  baseThemeState,
  updateThemeOverrides,
  computeThemeOverrides,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const canUseCustomCss = useCanUseFeature(Features.CUSTOM_CSS);

  useEffect(() => {
    if (!canUseCustomCss) return undefined;
    const handler = e => {
      if (e.detail === 'custom-css') setIsOpen(true);
    };
    window.addEventListener('expand-design-section', handler);
    return () => window.removeEventListener('expand-design-section', handler);
  }, [canUseCustomCss]);

  // Trailing control: a quiet lock when plan-gated, otherwise the expand chevron.
  const trailingControl = canUseCustomCss ? (
    <ChevronRight
      className={clsx(
        'h-3 w-3 text-gray-500 dark:text-muted-foreground transition-transform duration-200',
        {
          'rotate-90': isOpen,
        },
      )}
    />
  ) : (
    <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-muted-foreground" />
  );

  const header = (
    <div className="flex-1 text-left">
      <div className="flex items-center gap-x-2 relative">
        <Heading label="Custom CSS" level={3} icon={DESIGN_SECTION_ICONS['custom-css']} hideLabel />
        <div className="flex items-center gap-1 absolute top-0 right-0 z-0">
          <span className="p-1 h-6 w-6 flex items-center justify-center shrink-0">
            {trailingControl}
          </span>
        </div>
      </div>
      <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
        Add custom styles for advanced design tweaks.
      </div>
    </div>
  );

  // Plan-gated: the whole row is the click-to-upgrade trigger (its own quiet lock,
  // so CanUseFeature adds only the popover — no dimming, no second icon). The editor
  // never renders for free/trial sellers.
  if (!canUseCustomCss) {
    return (
      <div id="custom-css" className="mt-4 pb-32 text-sm">
        <CanUseFeature
          feature={Features.CUSTOM_CSS}
          isDisabled
          showIcon={false}
          reduceOpacity={false}
        >
          <div className="flex items-center gap-x-2 w-full">{header}</div>
        </CanUseFeature>
      </div>
    );
  }

  return (
    <div id="custom-css" className="mt-4 pb-32 text-sm">
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <Collapsible.Trigger asChild>
          <button type="button" className="flex items-center gap-x-2 w-full group">
            {header}
          </button>
        </Collapsible.Trigger>

        <Collapsible.Content className="CollapsibleContentAnimation">
          <div className="mt-4">
            <Form
              key={`css-${formKey}`}
              showState={false}
              autosave
              renderField={renderDesignField}
              customFieldPropsAreEqual={designFieldPropsAreEqual}
              customLeafFieldNames={getDesignLeafFieldNames}
              customContainerChildrenPropsEqual={designContainerChildrenPropsEqual}
              customContainerValuesEqual={designContainerValuesEqual}
              initialValues={{
                customCss: editorState.variables?.customCss,
              }}
              fields={[
                {
                  name: 'customCss',
                  type: 'css-editor',
                  placeholder: '/* Enter custom CSS here... */',
                },
              ]}
              onSubmit={async values => {
                const updatedValues = {
                  ...editorState,
                  variables: {
                    ...editorState.variables,
                    customCss: values.customCss,
                  },
                };
                const overrides = computeThemeOverrides(updatedValues, baseThemeState);

                await updateThemeOverrides.mutateAsync({
                  overrides: overrides,
                });

                return { data: null, error: null };
              }}
            />
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

const DesignForm = ({ galleryOpen = false, setGalleryOpen = () => {} } = {}) => {
  const { sellerId, seller } = useAuth();
  const { page, updatePage, refetch: refetchPage } = usePage();
  const { product } = useProduct();
  const { getThemes, updateTheme, createTheme, deleteTheme } = useThemeQueries();
  const { updateSeller } = useSellerQueries();
  const queryClient = useQueryClient();
  const { updatePage: updatePageApi } = usePageQueries();
  const [{ colorMode: previewColorMode }, previewDispatch] = usePreview();

  const [formKey, setFormKey] = useState(0);
  const [useOverrides, setUseOverrides] = useState(true);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const latestEditorValuesRef = useRef(null);

  const updateThemeOverrides = useMutation({
    mutationFn: ({ overrides }) => updatePageApi({ themeOverrides: overrides }, page._id),
    onMutate: async ({ overrides }) => {
      await queryClient.cancelQueries({ queryKey: pageKey(page._id) });

      const previousData = queryClient.getQueryData(pageKey(page._id));
      const updateTimestamp = Date.now();

      queryClient.setQueryData(pageKey(page._id), old => {
        if (old?._lastUpdateTimestamp && old._lastUpdateTimestamp > updateTimestamp) {
          return old;
        }

        return {
          ...old,
          themeOverrides: overrides,
          _lastUpdateTimestamp: updateTimestamp,
        };
      });

      return { previousData, updateTimestamp };
    },
    onError: (error, values, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(pageKey(page._id), context.previousData);
      }
      toast({
        title: 'Failed to apply overrides.',
        description: error.message,
        type: 'error',
      });
    },
  });

  const {
    data: themes,
    isLoading: isLoadingThemes,
    refetch: refetchThemes,
  } = useQuery({
    queryKey: themesKey(sellerId),
    queryFn: getThemes,
  });

  const MODERN_THEME_ID = '69b8c256c43b6a2080ee2652';

  const defaultThemeId = useMemo(
    () => themes?.find(theme => theme._id === MODERN_THEME_ID)?._id || themes?.[0]?._id,
    [themes],
  );

  const currentThemeId = useMemo(() => {
    const pageThemeId = page?.theme?._id;
    if (pageThemeId && themes?.some(t => t._id === pageThemeId)) return pageThemeId;
    return defaultThemeId;
  }, [page?.theme?._id, themes, defaultThemeId]);

  // Reset the ref when the theme or form key changes (user switched theme or discarded).
  useEffect(() => {
    latestEditorValuesRef.current = null;
  }, [currentThemeId, formKey]);

  const selectedTheme = useMemo(
    () => themes?.find(theme => theme._id === currentThemeId),
    [themes, currentThemeId],
  );

  const getThemeEditorState = useCallback(
    (theme, pageThemeOverrideVariables = {}) => {
      /*
       * We want to create an editor state that:
       * 1. includes sane defaults so the form never crashes due to undefined values
       * 2. starts with theme variables
       * 3. finally overrides with any page-specific overrides passed in
       */

      const mergedVariables = deepMergeWithBooleans(
        BASE_VARIABLES_DEFAULTS,
        theme?.variables || {},
        EDITOR_MERGE_OPTIONS,
      );

      let finalMergedVariables = deepMergeWithBooleans(
        mergedVariables,
        pageThemeOverrideVariables,
        EDITOR_MERGE_OPTIONS,
      );

      // If the theme doesn't set colorPrimary and the seller has a brand color,
      // use it as the default primary color (applies to both global and seller themes)
      if (
        seller?.brandColor &&
        !finalMergedVariables?.tokens?.colorPrimary?.light &&
        !pageThemeOverrideVariables?.tokens?.colorPrimary
      ) {
        finalMergedVariables = deepMergeWithBooleans(
          finalMergedVariables,
          {
            tokens: {
              colorPrimary: {
                light: seller.brandColor,
                dark: seller.brandColor,
              },
            },
          },
          EDITOR_MERGE_OPTIONS,
        );
      }

      return {
        name: theme?.name || 'Custom theme',
        enabled: true,
        variables: finalMergedVariables,
      };
    },
    [seller?.brandColor],
  );

  const isEditingOwnedCustomTheme = useMemo(() => {
    if (!selectedTheme) return false;

    return selectedTheme.scope === 'seller' && selectedTheme.scope !== 'global';
  }, [selectedTheme]);

  const baseThemeState = useMemo(() => {
    return getThemeEditorState(selectedTheme, {});
  }, [selectedTheme, getThemeEditorState]);

  const editorState = useMemo(() => {
    return getThemeEditorState(selectedTheme, useOverrides ? page?.themeOverrides?.variables : {});
  }, [selectedTheme, page?.themeOverrides?.variables, getThemeEditorState, useOverrides]);

  const hasChangesToDiscard = useMemo(() => {
    return !isEmpty(page?.themeOverrides?.variables);
  }, [page?.themeOverrides?.variables]);

  const handleSelectTheme = async theme => {
    if (theme._id === page?.theme?._id) return;

    let hasUnsavedChanges = false;
    const currentValues = latestEditorValuesRef.current;
    if (currentValues) {
      const diff = computeThemeOverrides(currentValues, baseThemeState);
      hasUnsavedChanges = !isEmpty(diff?.variables);
    } else {
      hasUnsavedChanges = !isEmpty(page?.themeOverrides?.variables);
    }

    if (hasUnsavedChanges) {
      const confirmed = await confirm('Discard changes?', {
        description:
          'You have unsaved changes. Cancel and save your changes to a new theme or discard them.',
        confirm: 'Discard changes',
        cancel: 'Cancel',
      });
      if (!confirmed) return;
    }

    latestEditorValuesRef.current = null;
    setFormKey(prev => prev + 1);

    await updatePage.mutateAsync({ theme, themeOverrides: { enabled: false } });
    toast({ title: `${theme.name || 'Theme'} applied to page successfully` });
  };

  const handleSetThemeAsDefault = async theme => {
    if (!theme?._id || seller?.defaultTheme === theme._id) return;
    const result = await updateSeller({ defaultTheme: theme._id });
    if (result?.error) {
      toast({
        title: 'Failed to set default theme.',
        description: result.error?.message || result.error,
        type: 'error',
      });
      return;
    }
    toast({ title: `${theme.name || 'Theme'} set as default theme` });
  };

  const handleDuplicateTheme = async theme => {
    const baseName = `${theme.name} copy`;
    let name = baseName;
    let i = 2;
    while (themes?.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      name = `${baseName} ${i++}`;
    }
    // Drop the visible selection immediately so the source card doesn't sit
    // selected at its new (shifted) position while we wait for the network.
    // The grid renders with nothing selected during the duplicate, then the
    // new card appears already selected once the cache writes land.
    setIsDuplicating(true);
    let previousPage;
    try {
      const newTheme = await createTheme({
        name,
        scope: 'seller',
        variables: theme.variables || {},
      });
      previousPage = queryClient.getQueryData(pageKey(page._id));
      // Batch the two cache writes so React Query flushes their observer
      // notifications together — v5's default scheduler defers each one via
      // setTimeout(0), which would otherwise paint between them.
      notifyManager.batch(() => {
        queryClient.setQueryData(themesKey(sellerId), old => [...(old || []), newTheme]);
        queryClient.setQueryData(pageKey(page._id), old => ({
          ...old,
          theme: newTheme,
          themeOverrides: { ...(old?.themeOverrides || {}), enabled: false },
        }));
      });
      setIsDuplicating(false);
      refetchThemes();
      latestEditorValuesRef.current = null;
      setFormKey(prev => prev + 1);
      await updatePage.mutateAsync({ theme: newTheme, themeOverrides: { enabled: false } });
      toast({ title: `Theme '${newTheme.name}' created and applied to page` });
    } catch (error) {
      setIsDuplicating(false);
      if (previousPage) {
        queryClient.setQueryData(pageKey(page._id), previousPage);
      }
      toast({ title: 'Failed to duplicate theme.', description: error.message, type: 'error' });
    }
  };

  const deleteThemeFlow = async theme => {
    if (!theme || theme.scope !== 'seller') return;
    const confirmed = await confirm('Delete this theme?', {
      confirm: 'Delete theme',
      cancel: 'Cancel',
    });
    if (!confirmed) return;

    const deletedId = theme._id;
    await deleteTheme(deletedId);
    queryClient.setQueryData(themesKey(sellerId), old =>
      (old || []).filter(t => t._id !== deletedId),
    );
    toast({ title: `Theme '${theme.name}' deleted.` });

    if (page?.theme?._id === deletedId || currentThemeId === deletedId) {
      const remainingThemes = themes?.filter(t => t._id !== deletedId);
      if (remainingThemes?.length > 0) {
        const fallbackTheme =
          remainingThemes.find(t => t._id === seller?.defaultTheme) ||
          remainingThemes.find(t => t._id === MODERN_THEME_ID) ||
          remainingThemes[0];
        await updatePage.mutateAsync({ theme: fallbackTheme, themeOverrides: { enabled: false } });
      }
    }

    refetchThemes();

    queryClient
      .getQueryCache()
      .findAll({ queryKey: ['pages'] })
      .forEach(query => {
        queryClient.setQueryData(query.queryKey, old => {
          if (!old) return old;
          const patchTheme = p => {
            const tid = typeof p.theme === 'string' ? p.theme : p.theme?._id;
            return tid === deletedId ? { ...p, theme: null } : p;
          };
          if (Array.isArray(old)) return old.map(patchTheme);
          return patchTheme(old);
        });
      });
  };

  async function handleDiscardChanges() {
    const confirmed = await confirm('Discard changes to this theme?', {
      confirm: 'Discard changes',
      cancel: 'Cancel',
    });
    if (!confirmed) return;
    await updatePage.mutateAsync({ themeOverrides: {} });
    latestEditorValuesRef.current = null;
    setUseOverrides(false);
    setFormKey(prev => prev + 1);
    setTimeout(() => setUseOverrides(true), 0);

    // Reset preview toggle to match the base theme (overrides are now cleared)
    const colorMode = getColorModeForScheme(baseThemeState?.variables?.tokens?.colors);
    previewDispatch({ type: 'setColorMode', value: colorMode });
  }

  async function handleSaveAsNewTheme() {
    if (!editorState || !editorState.variables) return;

    const initialThemeName =
      editorState.name && !themes?.find(t => t.name === editorState.name) ? editorState.name : ``;

    let defaultValue = initialThemeName;

    // Use computeThemeOverrides to get only the meaningful diff (strips out
    // system defaults like enabled:true, empty strings, default tokens, etc.),
    // then merge with the theme's actual stored values.
    const cleanOverrides = computeThemeOverrides(editorState, baseThemeState);
    const variablesToSave =
      stripSystemDefaults(
        deepMergeWithBooleans(
          selectedTheme?.variables || {},
          cleanOverrides?.variables || {},
          EDITOR_MERGE_OPTIONS,
        ),
      ) || {};

    // Loop so the user can retry with a different name if the server rejects
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let newThemeName;
      try {
        newThemeName = await prompt('Name your new theme', {
          placeholder: 'New theme name',
          defaultValue,
          confirmText: 'Save theme',
          cancelText: 'Cancel',
          validate: value => {
            if (!value || !value.trim()) return 'Please enter a theme name.';
            if (themes?.some(t => t.name.toLowerCase() === value.trim().toLowerCase()))
              return 'A theme with this name already exists. Please choose a different name.';
            return null;
          },
        });
      } catch {
        // User cancelled the prompt
        return;
      }

      try {
        const newTheme = await createTheme({
          name: newThemeName,
          scope: 'seller',
          variables: variablesToSave,
        });

        // Optimistically add the new theme to the cache so the dropdown
        // includes it immediately when the page update triggers a re-render
        queryClient.setQueryData(themesKey(sellerId), old => [...(old || []), newTheme]);

        await updatePage.mutateAsync({
          theme: newTheme,
          themeOverrides: { enabled: false },
        });

        latestEditorValuesRef.current = null;

        // Refetch in background for consistency with server state
        refetchThemes();

        toast({ title: `Theme '${newTheme.name}' created` });
        return;
      } catch (error) {
        // If server rejects (e.g. race condition duplicate), re-show prompt
        defaultValue = newThemeName;
        toast({
          title: 'A theme with this name already exists.',
          description: 'Please choose a different name.',
          type: 'error',
        });
      }
    }
  }

  async function handleApplyChanges() {
    if (
      !editorState ||
      !editorState.variables ||
      !selectedTheme ||
      selectedTheme.scope !== 'seller' ||
      selectedTheme.scope === 'global'
    ) {
      toast({
        title: 'Cannot update this theme.',
        description: 'Default themes or themes not owned by you cannot be updated directly.',
        type: 'error',
      });
      return;
    }

    const confirmed = await confirm('Apply changes to this theme?', {
      confirm: 'Apply changes',
      cancel: 'Cancel',
    });

    if (!confirmed) return;
    try {
      // Use computeThemeOverrides to get only the meaningful diff (strips out
      // system defaults like enabled:true, empty strings, default tokens, etc.),
      // then merge with the theme's actual stored values.
      const cleanOverrides = computeThemeOverrides(editorState, baseThemeState);
      const variablesToApply =
        stripSystemDefaults(
          deepMergeWithBooleans(
            selectedTheme?.variables || {},
            cleanOverrides?.variables || {},
            EDITOR_MERGE_OPTIONS,
          ),
        ) || {};

      const updated = await updateTheme({
        themeId: currentThemeId,
        values: { name: editorState.name, variables: variablesToApply },
      });

      // Optimistically update the theme in cache so that when overrides are
      // cleared below, editorState recomputes from the NEW theme values
      // instead of the stale cached ones (which would cause a flash).
      queryClient.setQueryData(themesKey(sellerId), old =>
        (old || []).map(t => (t._id === currentThemeId ? { ...t, ...updated } : t)),
      );

      toast({ title: `Theme '${updated.name}' updated.` });
      await updatePage.mutateAsync({ themeOverrides: {} });
      latestEditorValuesRef.current = null;
      refetchPage();
      refetchThemes();
    } catch (error) {
      toast({
        title: 'Failed to update theme.',
        description: error.message,
        type: 'error',
      });
    }
  }

  // Stable per input set: the Form keys a memo on this callback's identity, and an
  // inline one made it rebuild the whole field list on every render.
  const getGroupFields = useCallback(
    group =>
      ({ setFieldValue, values }) =>
        getDesignFormFields({
          themeValues: baseThemeState,
          values,
          setFieldValue,
          seller,
          page,
          product,
          group,
          previewColorMode,
        }),
    [baseThemeState, seller, page, product, previewColorMode],
  );
  const themeFields = useMemo(() => getGroupFields('theme'), [getGroupFields]);
  const elementFields = useMemo(() => getGroupFields('elements'), [getGroupFields]);

  const themesLoading = isLoadingThemes || !seller?._id;

  // While a duplicate is in flight we suppress the visible selection so the
  // source card doesn't look "selected at its new position" between insert
  // and the page-cache update.
  const displayedThemeId = isDuplicating ? null : currentThemeId;

  if (galleryOpen) {
    return (
      <div className="-mx-4 -mt-6">
        <AllThemesGallery
          themes={themes}
          currentThemeId={displayedThemeId}
          defaultThemeId={seller?.defaultTheme}
          colorMode={previewColorMode}
          onSelect={handleSelectTheme}
          onSetDefault={handleSetThemeAsDefault}
          onDuplicate={handleDuplicateTheme}
          onDelete={deleteThemeFlow}
          onClose={() => setGalleryOpen(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div id="layout" className="scroll-mt-20 mb-4">
        <Heading label="Layout" level={3} icon={DESIGN_SECTION_ICONS['layout']} />
        <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
          Choose how content is arranged on your hosted page.
        </div>
      </div>
      <LayoutForm />

      <Divider className="my-4" />

      <div id="theme" className="scroll-mt-20 mb-4">
        <Heading label="Themes" level={3} icon={DESIGN_SECTION_ICONS['theme']} />
        <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
          Select a theme, then fine-tune it to your needs
        </div>
      </div>

      <div className="mb-6">
        {themesLoading ? (
          <ThemeCardGridSkeleton />
        ) : (
          <ThemeCardGrid
            themes={themes}
            currentThemeId={displayedThemeId}
            defaultThemeId={seller?.defaultTheme}
            colorMode={previewColorMode}
            onSelect={handleSelectTheme}
            onViewAll={() => setGalleryOpen(true)}
            onSetDefault={handleSetThemeAsDefault}
            onDuplicate={handleDuplicateTheme}
            onDelete={deleteThemeFlow}
          />
        )}
      </div>

      <Divider className="my-4" />

      {selectedTheme && (
        <>
          <Form
            key={`theme-${formKey}`}
            enableReinitialize
            initialValues={editorState}
            autosave={true}
            autosaveDebounceMs={300}
            showState={false}
            renderField={renderDesignField}
            customFieldPropsAreEqual={designFieldPropsAreEqual}
            customLeafFieldNames={getDesignLeafFieldNames}
            customContainerChildrenPropsEqual={designContainerChildrenPropsEqual}
            customContainerValuesEqual={designContainerValuesEqual}
            onValuesChange={values => {
              latestEditorValuesRef.current = values;
            }}
            onSubmit={async values => {
              const overrides = computeThemeOverrides(values, baseThemeState);

              const { error } = await updateThemeOverrides.mutateAsync({
                overrides: overrides,
              });

              if (error) {
                logException(error);
                toast({
                  title: 'Failed to apply overrides',
                  description: 'Our team has been notified.',
                  type: 'error',
                });
              }

              return { data: null, error: null };
            }}
            fields={themeFields}
          />

          <CustomLabelsSection
            key={`labels-section-${formKey}`}
            formKey={formKey}
            seller={seller}
            editorState={editorState}
            baseThemeState={baseThemeState}
            updateThemeOverrides={updateThemeOverrides}
            computeThemeOverrides={computeThemeOverrides}
            enrichFieldsWithResetValues={enrichFieldsWithResetValues}
          />

          <Form
            key={`elements-${formKey}`}
            enableReinitialize
            initialValues={editorState}
            autosave={true}
            autosaveDebounceMs={300}
            showState={false}
            renderField={renderDesignField}
            customFieldPropsAreEqual={designFieldPropsAreEqual}
            customLeafFieldNames={getDesignLeafFieldNames}
            customContainerChildrenPropsEqual={designContainerChildrenPropsEqual}
            customContainerValuesEqual={designContainerValuesEqual}
            onValuesChange={values => {
              latestEditorValuesRef.current = values;
            }}
            onSubmit={async values => {
              const overrides = computeThemeOverrides(values, baseThemeState);

              const { error } = await updateThemeOverrides.mutateAsync({
                overrides: overrides,
              });

              if (error) {
                logException(error);
                toast({
                  title: 'Failed to apply overrides',
                  description: 'Our team has been notified.',
                  type: 'error',
                });
              }

              return { data: null, error: null };
            }}
            fields={elementFields}
          />

          <CustomCssSection
            key={`css-section-${formKey}`}
            formKey={formKey}
            editorState={editorState}
            baseThemeState={baseThemeState}
            updateThemeOverrides={updateThemeOverrides}
            computeThemeOverrides={computeThemeOverrides}
          />
        </>
      )}

      <div className="flex sticky bottom-0 bg-white dark:bg-background space-x-2 z-10 border-t border-gray-100 dark:border-border py-4 mt-0">
        <div>
          <DiscardChangesButton
            handleDiscardChanges={handleDiscardChanges}
            disabled={!hasChangesToDiscard}
          />
        </div>

        <div className="ml-auto flex space-x-2">
          {isEditingOwnedCustomTheme && (
            <Button appearance="secondary" onClick={handleApplyChanges} size="small">
              <Check className="w-4 h-4 mr-1.5" />
              Apply to theme
            </Button>
          )}
          <SaveAsNewThemeButton handleSaveAsNewTheme={handleSaveAsNewTheme} />
        </div>
      </div>
    </div>
  );
};

const LayoutForm = () => {
  const { page, updatePage } = usePage();

  return (
    <>
      <Form
        autosave
        autosaveDebounceMs={300}
        showState={false}
        initialValues={{
          hostedLayoutType: page.hostedLayoutType || 'center',
          formPosition: page.formPosition || 'left',
          backgroundWidth: page.backgroundWidth,
        }}
        fields={({ values }) => [
          {
            type: 'radioIconCard',
            name: 'hostedLayoutType',
            noMargin: values.hostedLayoutType === 'center',
            columns: 3,
            options: [
              {
                value: 'center',
                component: CenterCard,
                label: 'Center',
                center: true,
              },
              {
                value: 'double',
                component: DoubleCard,
                label: 'Double',
                center: true,
              },
              {
                value: 'split',
                component: SplitCard,
                label: 'Split',
                center: true,
              },
            ],
          },
        ]}
        onSubmit={async values => {
          const { data, error } = await updatePage.mutateAsync(values);

          storage.local.setItem('last-hosted-layout-type', values.hostedLayoutType);

          return {
            data,
            error,
          };
        }}
      />
    </>
  );
};

function DiscardChangesButton({ handleDiscardChanges, disabled }) {
  return (
    <Button
      onClick={handleDiscardChanges}
      appearance="secondary"
      size="small"
      disabled={disabled}
      className={disabled ? '!opacity-50' : ''}
    >
      <RefreshCw className="w-4 h-4 mr-1.5" />
      Discard changes
    </Button>
  );
}

function SaveAsNewThemeButton({ handleSaveAsNewTheme }) {
  return (
    <Button appearance="secondary" onClick={handleSaveAsNewTheme} size="small">
      <FilePlus className="w-4 h-4 mr-1.5" />
      Save as new
    </Button>
  );
}

function RadioIconCard({ children }) {
  return (
    <div className="flex items-center text-center justify-center w-full h-auto mb-0">
      {children}
    </div>
  );
}

export function DoubleCard() {
  return (
    <RadioIconCard>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 100 64" height="44">
        <rect
          width="48"
          height="64"
          fill="#000"
          fillOpacity=".08"
          className="dark:fill-foreground dark:[fill-opacity:0.15]"
          rx="2"
        />
        <rect
          width="48"
          height="64"
          x="52"
          fill="#000"
          fillOpacity=".08"
          className="dark:fill-foreground dark:[fill-opacity:0.15]"
          rx="2"
        />
      </svg>
    </RadioIconCard>
  );
}

export function CenterCard() {
  return (
    <RadioIconCard>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 64" height="44">
        <rect
          width="48"
          height="64"
          fill="#000"
          fillOpacity=".08"
          className="dark:fill-foreground dark:[fill-opacity:0.15]"
          rx="2"
        />
      </svg>
    </RadioIconCard>
  );
}

export function SplitCard() {
  return (
    <RadioIconCard>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 100 64" height="44">
        <rect
          x="52"
          width="48"
          height="64"
          fill="#000"
          fillOpacity=".08"
          className="dark:fill-foreground dark:[fill-opacity:0.15]"
          rx="2"
        />
      </svg>
    </RadioIconCard>
  );
}

export default DesignForm;
